import { Box, SxProps, Theme, Typography } from '@mui/material';
import React, { ReactElement, useLayoutEffect, useState } from 'react';
import {
  tooltipStyles,
  tooltipListStyles,
  tooltipListItemStyles,
  tooltipIconStyles,
  tooltipItemTextStyles,
} from './TooltipErrorsProps';
import { measureTooltipWidth } from './measureTooltipWidth';
import { ComponentTestIds } from '../../utils/dataTestIds';
import Tooltip from '../Tooltip/Tooltip';
import ErrorIcon from '../../assets/icons/ErrorIcon';
import WarningIcon from '../../assets/icons/Warning';

export type TooltipProps = 'top' | 'right' | 'left' | 'bottom';
interface Props {
  children: ReactElement<unknown>;
  errors: string[];
  warnings?: string[];
  tooltipPlacement?: TooltipProps;
  tooltipSx?: SxProps<Theme>;
  fitContent?: boolean;
  type?: 'error' | 'warning';
}

const TooltipErrors: React.FC<Props> = ({
  children,
  errors,
  warnings,
  tooltipPlacement = 'top',
  tooltipSx,
  fitContent = true,
  type = 'error',
}: Props) => {
  const [computedWidth, setComputedWidth] = useState<number | undefined>(
    undefined
  );

  useLayoutEffect(() => {
    setComputedWidth(
      fitContent
        ? measureTooltipWidth([...errors, ...(warnings || [])])
        : undefined
    );
  }, [errors, warnings, fitContent]);
  const renderTitle = () => {
    return (
      <Box
        sx={tooltipListStyles}
        data-testid={ComponentTestIds.TooltipErrors.Message}
      >
        {errors.map((message, index) => (
          <Box key={index} sx={tooltipListItemStyles}>
            <Box sx={tooltipIconStyles}>
              <ErrorIcon />
            </Box>
            <Typography
              component="span"
              variant="body2"
              sx={tooltipItemTextStyles}
            >
              <span dangerouslySetInnerHTML={{ __html: message }} />
            </Typography>
          </Box>
        ))}
        {warnings?.map((message, index) => (
          <Box key={errors.length + index} sx={tooltipListItemStyles}>
            <Box sx={tooltipIconStyles}>
              <WarningIcon />
            </Box>
            <Typography
              component="span"
              variant="body2"
              sx={tooltipItemTextStyles}
            >
              <span dangerouslySetInnerHTML={{ __html: message }} />
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Tooltip
      className="tooltip-errors"
      data-testid={ComponentTestIds.TooltipErrors.ErrorIcon}
      title={renderTitle()}
      arrow
      placement={tooltipPlacement}
      slotProps={{
        tooltip: {
          sx: [
            tooltipStyles({ type }),
            tooltipSx,
            computedWidth != null ? { width: computedWidth } : {},
          ] as SxProps<Theme>,
        },
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset:
                  tooltipPlacement === 'top'
                    ? [0, -4]
                    : tooltipPlacement === 'right'
                      ? [0, 13]
                      : [0, 0],
              },
            },
            {
              name: 'preventOverflow',
              options: {
                padding: 16,
                altAxis: true,
              },
            },
            {
              name: 'arrow',
              options: {
                padding: 6,
              },
            },
            {
              name: 'flip',
              enabled: false,
            },
          ],
        },
      }}
    >
      <Box component={'span'} sx={{ marginBottom: '-6px' }}>
        {children}
      </Box>
    </Tooltip>
  );
};

export default TooltipErrors;
